/**
 * Unit conversion data for the RPN Calculator
 * Ported from legacy Utils.setConversionProperties
 */

export interface ConversionProperty {
  name: string;
  units: string[];
  factors: (number | string)[];
}

export const CONVERSION_PROPERTIES: ConversionProperty[] = [
  {
    name: 'Acceleration',
    units: ['Meter/sq.sec (m/sec\u00B2)', 'Foot/sq.sec (ft/sec\u00B2)', 'G (g)', 'Galileo (gal)', 'Inch/sq.sec (in/sec\u00B2)', 'Yard/sq.sec (yd/sec\u00B2)'],
    factors: [1, 0.3048, 9.806650, 0.01, 2.54e-02, 0.9144]
  },
  {
    name: 'Area',
    units: ['Square meter (m\u00B2)', 'Acre (acre)', 'Are', 'Barn (barn)', 'Hectare', 'Rood', 'Square centimeter', 'Square kilometer', 'Circular mil', 'Square foot (ft\u00B2)', 'Square inch (in\u00B2)', 'Square mile (mi\u00B2)', 'Square yard (yd\u00B2)', 'Arpent', 'Cuerda', 'Plaza', 'Square Perch/Pole/Rod', 'Strema'],
    factors: [1, 4046.856, 100, 1e-28, 10000, 1011.71413184285, 0.0001, 1000000, 5.067075e-10, 9.290304e-02, 6.4516e-04, 2589988, 0.8361274, 4041.28224, 3930.395625, 6400, 25.29285264, 1000]
  },
  {
    name: 'Torque',
    units: ['Newton-meter (N m)', 'Dyne-centimeter(dy cm)', 'Kgrf-meter (kgf m)', 'lbf-inch (lbf in)', 'lbf-foot (lbf ft)'],
    factors: [1, 0.0000001, 9.806650, 0.1129848, 1.355818]
  },
  {
    name: 'Electricity',
    units: ['Coulomb (Cb)', 'Abcoulomb', 'Ampere hour (A hr)', 'Faraday (F)', 'Statcoulomb', 'Millifaraday (mF)', 'Microfaraday (mu-F)', 'Nanofaraday (nF)'],
    factors: [1, 10, 3600, 96521.8999999997, 0.000000000333564, 96.5219, 9.65219e-02, 9.65219e-05]
  },
  {
    name: 'Energy',
    units: ['Joule (J)', 'BTU (mean)', 'BTU (thermochemical)', 'Calorie (SI) (cal)', 'Calorie (mean)(cal)', 'Calorie (thermo)', 'Electron volt (eV)', 'Erg (erg)', 'Foot-pound force', 'Foot-poundal', 'Horsepower-hour', 'Kilocalorie (SI)(kcal)', 'Kilocalorie (mean)(kcal)', 'Kilowatt-hour (kW hr)', 'Ton of TNT', 'Volt-coulomb (V Cb)', 'Watt-hour (W hr)', 'Watt-second (W sec)'],
    factors: [1, 1055.87, 1054.35, 4.1868, 4.19002, 4.184, 1.6021e-19, 0.0000001, 1.355818, 4.214011e-02, 2684077.3, 4186.8, 4190.02, 3600000, 4.2e9, 1, 3600, 1]
  },
  {
    name: 'Force',
    units: ['Newton (N)', 'Dyne (dy)', 'Kilogram force (kgf)', 'Kilopond force (kpf)', 'Kip (k)', 'Ounce force (ozf)', 'Pound force (lbf)', 'Poundal'],
    factors: [1, 0.00001, 9.806650, 9.806650, 4448.222, 0.2780139, 4.4482216153, 0.138255]
  },
  {
    name: 'Force / Length',
    units: ['Newton/meter (N/m)', 'Pound force/inch (lbf/in)', 'Pound force/foot (lbf/ft)'],
    factors: [1, 175.1268, 14.5939]
  },
  {
    name: 'Length',
    units: ['Meter (m)', 'Angstrom (A\')', 'Astronomical unit (AU)', 'Caliber (cal)', 'Centimeter (cm)', 'Kilometer (km)', 'Ell', 'Em', 'Fathom', 'Furlong', 'Fermi (fm)', 'Foot (ft)', 'Inch (in)', 'League (int\'l)', 'League (UK)', 'Light year (LY)', 'Micrometer (mu-m)', 'Mil', 'Millimeter (mm)', 'Nanometer (nm)', 'Mile (int\'l nautical)', 'Mile (UK nautical)', 'Mile (US nautical)', 'Mile (US statute)', 'Parsec', 'Pica (printer)', 'Picometer (pm)', 'Point (pt)', 'Rod', 'Yard (yd)'],
    factors: [1, 1e-10, 1.49598e11, 0.000254, 0.01, 1000, 1.143, 4.2323e-03, 1.8288, 201.168, 1e-15, 0.3048, 0.0254, 5556, 5556, 9.46055e+15, 0.000001, 0.0000254, 0.001, 1e-9, 1852, 1853.184, 1852, 1609.344, 3.08374e+16, 4.217518e-03, 1e-12, 0.0003514598, 5.0292, 0.9144]
  },
  {
    name: 'Light',
    units: ['Lumen/sq.meter (lm/m\u00B2)', 'Lumen/sq.centimeter', 'Lumen/sq.foot', 'Foot-candle (fc)', 'Foot-lambert', 'Candela/sq.meter', 'Candela/sq.centimeter', 'Lux (lux)', 'Phot'],
    factors: [1, 10000, 10.76391, 10.76391, 10.76391, 3.14159250538575, 31415.9250538576, 1, 10000]
  },
  {
    name: 'Mass',
    units: ['Kilogram (kgr)', 'Gram (gr)', 'Milligram (mgr)', 'Microgram (mu-gr)', 'Carat (metric)(ct)', 'Hundredweight (long)', 'Hundredweight (short)', 'Pound mass (lbm)', 'Pound mass (troy)', 'Ounce mass (ozm)', 'Ounce mass (troy)', 'Slug', 'Ton (assay)', 'Ton (long)', 'Ton (short)', 'Ton (metric)', 'Tonne'],
    factors: [1, 0.001, 1e-6, 0.000000001, 0.0002, 50.80235, 45.35924, 0.4535924, 0.3732417, 0.02834952, 0.03110348, 14.5939, 0.02916667, 1016.047, 907.1847, 1000, 1000]
  },
  {
    name: 'Mass Flow',
    units: ['Kilogram/second (kgr/sec)', 'Pound mass/sec (lbm/sec)', 'Pound mass/min (lbm/min)'],
    factors: [1, 0.4535924, 0.007559873]
  },
  {
    name: 'Density & Mass capacity',
    units: ['Kilogram/cub.meter', 'Grain/galon', 'Grams/cm\u00B3 (gr/cc)', 'Pound mass/cubic foot', 'Pound mass/cubic-inch', 'Ounces/gallon (UK,liq)', 'Ounces/gallon (US,liq)', 'Ounces (mass)/inch', 'Pound mass/gal (UK,liq)', 'Pound mass/gal (US,liq)', 'Slug/cubic foot', 'Tons (long,mass)/cub.yard'],
    factors: [1, 0.01711806, 1000, 16.01846, 27679.91, 6.236027, 7.489152, 1729.994, 99.77644, 119.8264, 515.379, 1328.939]
  },
  {
    name: 'Power',
    units: ['Watt (W)', 'Kilowatt (kW)', 'Megawatt (MW)', 'Milliwatt (mW)', 'BTU (SI)/hour', 'BTU (thermo)/second', 'BTU (thermo)/minute', 'BTU (thermo)/hour', 'Calorie (thermo)/second', 'Calorie (thermo)/minute', 'Erg/second', 'Foot-pound force/hour', 'Foot-pound force/minute', 'Foot-pound force/second', 'Horsepower(550 ft lbf/s)', 'Horsepower (electric)', 'Horsepower (boiler)', 'Horsepower (metric)', 'Horsepower (UK)', 'Kilocalorie (thermo)/min', 'Kilocalorie (thermo)/sec'],
    factors: [1, 1000, 1000000, 0.001, 0.2930667, 1054.35, 17.5725, 0.2928751, 4.184, 6.973333e-02, 0.0000001, 0.0003766161, 0.02259697, 1.355818, 745.7, 746, 9809.5, 735.499, 745.7, 69.7333, 4184]
  },
  {
    name: 'Pressure & Stress',
    units: ['Newton/sq.meter', 'Atmosphere (normal)', 'Atmosphere (techinical)', 'Bar', 'Centimeter mercury(cmHg)', 'Centimeter water (4\'C)', 'Decibar', 'Kgr force/sq.centimeter', 'Kgr force/sq.meter', 'Kip/square inch', 'Millibar', 'Millimeter mercury(mmHg)', 'Pascal (Pa)', 'Kilopascal (kPa)', 'Megapascal (Mpa)', 'Poundal/sq.foot', 'Pound-force/sq.foot', 'Pound-force/sq.inch (psi)', 'Torr (mmHg,0\'C)'],
    factors: [1, 101325, 98066.5, 100000, 1333.22, 98.0638, 10000, 98066.5, 9.80665, 6894757, 100, 133.3224, 1, 1000, 1000000, 47.88026, 47.88026, 6894.757, 133.322]
  },
  {
    name: 'Temperature',
    units: ['Degrees Celsius (\u00B0C)', 'Degrees Fahrenheit (\u00B0F)', 'Degrees Kelvin (\u00B0K)', 'Degrees Rankine (\u00B0R)'],
    factors: ['DC', 'DF', 'DK', 'DR']
  },
  {
    name: 'Time',
    units: ['Second (sec)', 'Day (mean solar)', 'Day (sidereal)', 'Hour (mean solar)', 'Hour (sidereal)', 'Minute (mean solar)', 'Minute (sidereal)', 'Month (mean calendar)', 'Second (sidereal)', 'Year (calendar)', 'Year (tropical)', 'Year (sidereal)'],
    factors: [1, 8.640e4, 86164.09, 3600, 3590.17, 60, 60, 2628000, 0.9972696, 31536000, 31556930, 31558150]
  },
  {
    name: 'Velocity & Speed',
    units: ['Meter/second (m/sec)', 'Foot/minute (ft/min)', 'Foot/second (ft/sec)', 'Kilometer/hour (kph)', 'Knot (int\'l)', 'Mile (US)/hour (mph)', 'Mile (nautical)/hour', 'Mile (US)/minute', 'Mile (US)/second', 'Speed of light (c)', 'Mach (STP)(a)'],
    factors: [1, 5.08e-03, 0.3048, 0.2777778, 0.5144444, 0.44704, 0.514444, 26.8224, 1609.344, 299792458, 340.0068750]
  },
  {
    name: 'Viscosity',
    units: ['Newton-second/meter', 'Centipoise', 'Centistoke', 'Sq.foot/second', 'Poise', 'Poundal-second/sq.foot', 'Pound mass/foot-second', 'Pound force-second/sq.foot', 'Rhe', 'Slug/foot-second', 'Stoke'],
    factors: [1, 0.001, 0.000001, 9.290304e-02, 0.1, 1.488164, 1.488164, 47.88026, 10, 47.88026, 0.0001]
  },
  {
    name: 'Volume & Capacity',
    units: ['Cubic Meter (m\u00B3)', 'Cubic centimeter', 'Cubic millimeter', 'Acre-foot', 'Barrel (oil)', 'Board foot', 'Bushel (US)', 'Cup', 'Fluid ounce (US)', 'Cubic foot', 'Gallon (UK)', 'Gallon (US,dry)', 'Gallon (US,liq)', 'Gill (UK)', 'Gill (US)', 'Cubic inch (in\u00B3)', 'Liter (new)', 'Liter (old)', 'Ounce (UK,fluid)', 'Ounce (US,fluid)', 'Peck (US)', 'Pint (US,dry)', 'Pint (US,liq)', 'Quart (US,dry)', 'Quart (US,liq)', 'Stere', 'Tablespoon', 'Teaspoon', 'Ton (register)', 'Cubic yard'],
    factors: [1, 0.000001, 0.000000001, 1233.482, 0.1589873, 0.002359737, 0.03523907, 0.0002365882, 0.00002957353, 0.02831685, 0.004546087, 0.004404884, 0.003785412, 0.0001420652, 0.0001182941, 0.00001638706, 0.001, 0.001000028, 0.00002841305, 0.00002957353, 8.8097680e-03, 0.0005506105, 4.7317650e-04, 0.001101221, 9.46353e-04, 1, 0.00001478676, 0.000004928922, 2.831685, 0.7645549]
  },
  {
    name: 'Volume Flow',
    units: ['Cubic meter/second', 'Cubic foot/second', 'Cubic foot/minute', 'Cubic inches/minute', 'Gallons (US,liq)/minute)'],
    factors: [1, 0.02831685, 0.0004719474, 2.731177e-7, 6.309020e-05]
  }
];

const TEMPERATURE_INDEX = 14;

/**
 * Convert a value between units.
 * For temperature, fromValue/toValue are factor strings ('DC','DF','DK','DR').
 * For all other properties, they are numeric indices into the factors array.
 */
export function convertValue(
  value: number,
  propertyIndex: number,
  fromValue: number | string,
  toValue: number | string
): number {
  const property = CONVERSION_PROPERTIES[propertyIndex];
  if (!property) return value;

  // Temperature requires special handling
  if (propertyIndex === TEMPERATURE_INDEX) {
    return convertTemperature(value, String(fromValue), String(toValue));
  }

  const fromFactor = property.factors[Number(fromValue)] as number;
  const toFactor = property.factors[Number(toValue)] as number;

  return (value * fromFactor) / toFactor;
}

function convertTemperature(value: number, from: string, to: string): number {
  // Convert to Celsius first, then to target
  let celsius: number;

  switch (from) {
    case 'DC': celsius = value; break;
    case 'DF': celsius = (value - 32) / 1.8; break;
    case 'DK': celsius = value - 273.15; break;
    case 'DR': celsius = (value - 491.67) * (5 / 9); break;
    default: return value;
  }

  switch (to) {
    case 'DC': return celsius;
    case 'DF': return (celsius * 1.8) + 32;
    case 'DK': return celsius + 273.15;
    case 'DR': return (celsius + 273.15) * 1.8;
    default: return value;
  }
}
